import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axiosInstance from 'api/config/axios';
import { ProductPropertyTypeCode, TradeStatusAttributeCode } from 'utils/enums';
import { useDataValues } from 'providers/DataValues';
import { useAppState } from 'providers/AppState';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { parseManyResponse } from '../../parsers';
import {
  AimeosEntityType,
  AimeosManyResponse,
  AimeosProduct,
  AimeosProductFilterBy,
  AimeosProductType,
} from '../../types';
import { parseOrder } from '../../order.parsers';

type Params = {
  page?: number;
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: AimeosProductFilterBy | null;
  orderId?: string;
  statusAttrIds?: string[];
  catalogId?: string;
  attrIds?: string[];
  userId: string;
};

const PAGE_AMOUNT = 10;

async function getMyTrades({
  page,
  sortOrder,
  sortBy,
  orderId,
  statusAttrIds,
  catalogId,
  attrIds,
  userId,
}: Params & { page?: number }) {
  const order = sortOrder === 'DESC' ? '-' : '';
  const sort = sortBy ? `${order}${sortBy.toString()}` : ``;
  const filteredAttrs = attrIds?.filter((a) => a && a !== 'All');

  const params = {
    include: 'product/property,attribute,catalog',
    page: {
      limit: PAGE_AMOUNT,
      offset: PAGE_AMOUNT * (page || 0),
    },
    sort,
    filter: {
      '&&': {
        0: {
          '||': statusAttrIds?.map((id) => ({ '!=': { [`product:has("attribute","default","${id}")`]: null } })),
        },
        1: {
          '&&': [
            { '==': { 'product.status': 1 } },
            { '==': { 'product.type': AimeosProductType.Trade } },
            orderId?.length ? { '~=': { 'product.code': orderId } } : null,
            { '!=': { [`product:prop("${ProductPropertyTypeCode.TradeCreator}",null,"${userId}")`]: null } },
            catalogId && catalogId !== 'All'
              ? { '!=': { [`product:has2("catalog","default","${catalogId}")`]: null } }
              : null,
            ...(filteredAttrs || []).map((attrId, index) => ({
              '!=': { [`product:has${3 + index}("attribute","default","${attrId}")`]: null },
            })),
          ].filter(Boolean),
        },
      },
    },
  };

  const response = await axiosInstance.query.get<AimeosManyResponse<AimeosProduct>>('/admin/default/jsonadm/product', {
    params,
  });

  return response.data;
}

export const myTradesSelector = (result: Awaited<ReturnType<typeof getMyTrades>>) => {
  const parsedResponse = parseManyResponse<AimeosEntityType.Product>(result);

  return parsedResponse.map(parseOrder);
};

export type MyTradeSelectorItem = ReturnType<typeof myTradesSelector>[0];

type InfiniteParams = {
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: AimeosProductFilterBy | null;
  orderId?: string;
  statusId?: string;
  excludeStatus?: TradeStatusAttributeCode;
  attrIds?: string[];
  statusAttrIds?: string[];
  catalogId?: string;
};

export const myTradesAllQueryKey = ['product', 'my-trades'] as const;

export const getMyTradesInfiniteQueryKey = (params: InfiniteParams) =>
  [
    ...myTradesAllQueryKey,
    'list',
    params.sortBy,
    params.sortOrder,
    params.orderId,
    params.statusId,
    params.excludeStatus,
    params.catalogId,
    params.attrIds?.join('-'),
    params.statusAttrIds?.join('-'),
  ] as const;

export function useGetMyTradesInfiniteQuery(params: InfiniteParams, enabled = true) {
  const { blockRequests } = useAppState();
  const { profile, appValues } = useDataValues();

  const excludeStatusId = params.excludeStatus
    ? appValues?.attributes?.tradeStatus?.find((a) => a.code === params.excludeStatus)?.id
    : '';

  const statusAttrIds =
    params.statusId && params.statusId !== 'All'
      ? [params.statusId]
      : appValues?.attributes?.tradeStatus?.map((a) => a.id)?.filter((a) => a !== excludeStatusId);

  const fullParams = {
    ...params,
    userId: profile?.id?.toString() || '',
    statusAttrIds,
  };

  return useInfiniteQuery(
    {
      queryKey: getMyTradesInfiniteQueryKey(fullParams),
      queryFn: ({ pageParam = 0 }) => getMyTrades({ ...fullParams, page: pageParam }),
      initialPageParam: 0,
      enabled: !blockRequests && !!fullParams.userId && enabled && excludeStatusId !== params.statusId,
      getNextPageParam: (lastPage, pages) => {
        const total = lastPage.meta.total;
        const lastPageCount = pages.length;
        const remainingItems = total - lastPageCount * PAGE_AMOUNT;

        if (remainingItems > 0) {
          return lastPageCount;
        }
        return undefined;
      },
      refetchOnWindowFocus: 'always',
      networkMode: 'online',
      gcTime: 15000,
      staleTime: 0,
    },
    memoryQueryClient,
  );
}

type RegularParams = {
  sortOrder?: 'ASC' | 'DESC';
  sortBy?: AimeosProductFilterBy | null;
  orderId?: string;
  status?: TradeStatusAttributeCode;
  selectedStatusId?: string;
  attrIds?: string[];
  statusAttrIds?: string[];
  catalogId?: string;
};

export const getMyTradesQueryKey = (params: RegularParams) =>
  [
    ...myTradesAllQueryKey,
    'traded',
    params.sortBy,
    params.sortOrder,
    params.orderId,
    params.selectedStatusId,
    params.catalogId,
    params.attrIds?.join('-'),
    params.statusAttrIds?.join('-'),
  ] as const;

export function useGetMyTradesQuery(params: RegularParams, enabled = true) {
  const { blockRequests } = useAppState();
  const { profile, appValues } = useDataValues();
  const statusId = params.status ? appValues?.attributes?.tradeStatus?.find((a) => a.code === params.status)?.id : '';
  const statusAttrIds = statusId ? [statusId] : appValues?.attributes?.tradeStatus?.map((a) => a.id);
  const fullParams = { ...params, userId: profile?.id?.toString() || '', statusAttrIds };

  return useQuery(
    {
      queryKey: getMyTradesQueryKey(fullParams),
      queryFn: () => getMyTrades(fullParams),
      select: myTradesSelector,
      enabled:
        !blockRequests &&
        !!fullParams.userId &&
        enabled &&
        (params.selectedStatusId === statusId || params.selectedStatusId === 'All'),
    },
    memoryQueryClient,
  );
}
